import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const redirectTo = location.state?.from?.pathname || "/";

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-brand-paper flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <img src="/picto-instant.png" alt="Agence Instant" className="h-9 w-9 object-contain" />
          <div className="h-9 w-px bg-brand-line" />
          <div className="leading-none">
            <div className="font-display text-xl text-brand-ink">Instant Studio</div>
            <div className="eyebrow mt-1">by Agence Instant</div>
          </div>
        </div>

        <div className="rounded-lg border border-brand-line bg-white p-8 shadow-card">
          <div className="flex items-center gap-2 text-brand-moss mb-6">
            <Lock className="h-4 w-4" strokeWidth={1.5} />
            <span className="eyebrow">Accès privé</span>
          </div>
          <h1 className="font-display text-3xl text-brand-ink">Connexion</h1>
          <p className="mt-2 text-sm text-brand-subtle">
            Cet espace est réservé à Agence Instant.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@instant-agence.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
            </Button>

            <div className="pt-2 text-center">
              <Link to="/forgot-password" className="text-sm text-brand-subtle hover:text-brand-ink underline underline-offset-4" data-testid="forgot-password-link">
                Mot de passe oublié ?
              </Link>
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-brand-subtle">
          Agence Instant · Instant Studio
        </p>
      </div>
    </div>
  );
}
